import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { Switch } from '@multaqa/ui/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@multaqa/ui/components/ui/tooltip'
import { localizedText } from '@/lib/localized-text'
import type { CompanyOption } from './api'

export function CompanyTextField({
  id,
  label,
  value,
  dir,
  onChange,
  type = 'text',
  min,
  max,
}: {
  id: string
  label: string
  value: string
  dir: 'ltr' | 'rtl'
  onChange: (value: string) => void
  type?: 'text' | 'number'
  min?: string
  max?: string
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        min={min}
        max={max}
        numeric={type === 'number'}
        value={value}
        dir={dir}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

export function CompanyReadOnlyField({
  label,
  value,
  dir,
  testId,
}: {
  label: string
  value: string
  dir?: 'ltr' | 'rtl'
  testId?: string
}) {
  return (
    <div className="border-b border-border py-3" data-testid={testId}>
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      <div className="text-body text-foreground" dir={dir}>
        {value}
      </div>
    </div>
  )
}

export function CompanyCurrencyField({ value }: { value: string }) {
  const { t } = useTranslation()
  return (
    <div className="border-b border-border py-3">
      <div className="text-label text-muted-foreground font-mono uppercase">
        {t('companies.form.baseCurrency')}
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-identifier cursor-help border-b border-border font-mono">
              {value}
            </span>
          </TooltipTrigger>
          <TooltipContent>{t('companies.form.baseCurrencyReadOnly')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

export function CompanySelectField({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
}: {
  id: string
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={`${id}-trigger`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function CompanyToggleField({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <Label htmlFor={id}>{label}</Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

export function CompanyParentField({
  value,
  options,
  locale,
  loading,
  error,
  onRetry,
  onChange,
}: {
  value: string
  options: CompanyOption[]
  locale: string
  loading: boolean
  error: boolean
  onRetry: () => void
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="grid gap-1.5 sm:col-span-2">
      <Label htmlFor="company-overview-parent">{t('companies.form.parent')}</Label>
      {error ? (
        <div className="flex items-center gap-2 text-small text-danger" role="alert">
          <span>{t('companies.form.parentLoadFailed')}</span>
          <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
            {t('companies.form.retryParents')}
          </Button>
        </div>
      ) : (
        <Select
          value={value || '__none__'}
          onValueChange={(next) => onChange(next === '__none__' ? '' : next)}
        >
          <SelectTrigger id="company-overview-parent" data-testid="company-overview-parent-trigger">
            <SelectValue placeholder={t('companies.form.parentNone')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t('companies.form.parentNone')}</SelectItem>
            {loading
              ? null
              : options.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {localizedText(option.name, locale)}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
